import { useEffect, useRef } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'

export const POSITION_MOVEMENT_HOTKEYS =
  'up, down, left, right, shift+up, shift+down, shift+left, shift+right, shift+ctrl+up, shift+ctrl+down, shift+ctrl+left, shift+ctrl+right'

export const getMovedPosition = ({
  key,
  shiftKey,
  ctrlKey,
  position,
  pageDimensions,
  speed = 1,
}) => {
  const { x, y, width, height } = position
  const { originalHeight = height, originalWidth = width } = pageDimensions
  const moveBy = shiftKey ? Math.min(speed * 10, 100) : Math.min(speed, 20)
  const maxX = Math.max(originalWidth - width, 0)
  const maxY = Math.max(originalHeight - height, 0)

  const keyMap = {
    ArrowUp: { y: ctrlKey ? 0 : Math.max(y - moveBy, 0) },
    ArrowDown: {
      y: ctrlKey ? maxY : Math.min(y + moveBy, maxY),
    },
    ArrowLeft: { x: ctrlKey ? 0 : Math.max(x - moveBy, 0) },
    ArrowRight: {
      x: ctrlKey ? maxX : Math.min(x + moveBy, maxX),
    },
  }

  return keyMap[key]
}

export const usePositionHotkeys = ({
  isActive,
  position,
  pageDimensions,
  onMove,
  dependencies = [],
}) => {
  const speedRef = useRef(1)
  const repeatCountRef = useRef(0)
  const requestRef = useRef(null)

  useEffect(
    () => () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current)
      }
    },
    []
  )

  useHotkeys(
    POSITION_MOVEMENT_HOTKEYS,
    e => {
      if (!isActive) return

      if (e.repeat) {
        repeatCountRef.current++
        speedRef.current = ~~Math.min(
          15,
          1 + Math.pow(repeatCountRef.current, 1.5) / 5
        )
      } else {
        repeatCountRef.current = 0
        speedRef.current = 1
      }

      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation?.()

      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current)
      }

      requestRef.current = requestAnimationFrame(() => {
        const newPosition = getMovedPosition({
          key: e.key,
          shiftKey: e.shiftKey,
          ctrlKey: e.ctrlKey,
          position,
          pageDimensions,
          speed: speedRef.current,
        })

        if (newPosition) onMove(newPosition)
      })
    },
    [isActive, position, pageDimensions, onMove, ...dependencies]
  )
}
